import React, { useEffect, useRef, useState } from "react";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import { Headings } from "@arctheme-components/elements/Headings/Headings";
import { Paragraph } from "@project-stories/elements/Paragraph/Paragraph";
import { StarIcon } from "../TestimonialStar/TestimonialStar";
import { Image } from "@arctheme-components/elements/Image/Image";
import clsx from "clsx";

const ScrollingLayout = ({ settings, blocks }) => {
  const isMobile = useMobileBreakpoint(settings.mobile_breakpoint);
  const { section } = settings;
  
  const sideLayout = section.side_by_side_layout && !isMobile;
  const mosaicGrid = isMobile;
  

  const textStyle = isMobile ? section.testimonial_quote_style_mobile : section.testimonial_quote_style;
  const authorStyle = isMobile ? section.testimonial_author_style_mobile  : section.testimonial_author_style;
  const subTextType = isMobile ? section.testimonial_subtext_style_mobile  : section.testimonial_subtext_style;
  const paddingleftRight = isMobile ? section.padding_left_right_mobile || 0 :  section.padding_left_right_desktop || 16;

  const contentBeneathTitle = section.position_subtext_beneath_title;
  const enableReadMore = section.enable_read_more && section.read_more_max_chars > 0;
  const leftColumnWidth = section.left_column_width || 50;
  const rightColumnWidth = 100 - leftColumnWidth;
  const contentGap = isMobile ? section.content_gap_mobile : section.content_gap;
  const blockSpacing = isMobile ? section. block_spacing_mobile : section.block_spacing;
 
  const starColor = section.star_color || "#FFD700"; // Default golden color if not specified
  
  // Custom rendering for average rating if enabled
  const showAverageRating = section.show_average_rating;
  const averageRating = showAverageRating ? 
    blocks.reduce((acc, block) => acc + (block.settings.star_rating || 0), 0) / blocks.length : 0;
  

  const truncateText = (text) => {
    return text;
  };

  // Animation speed (in seconds) - can be controlled via a setting if needed
  const scrollSpeed = section.scroll_speed || 10;
  const scrollSpeedReverse = section.scroll_speed_reverse || scrollSpeed;
  const CARD_HEIGHT = 348; 
  const scrollDistancePx = (blocks.length  * CARD_HEIGHT);


  return (
    <div className={clsx("testimonials testimonials-scrolling", {
      "side-by-side !flex-row px-[80px]": sideLayout,
      "mosaic-grid px-[20px]": mosaicGrid
    })}>
      <div className={clsx(
        `testimonials-header flex flex-col lg:min-w-[500px] pt-[80px] pb-[40px] lg:mx-[20px] px-[${paddingleftRight}px] text-left justify-start w-full`,
        `text-${section.content_alignment_desktop}`, `gap-[${contentGap}px]`
      )}>
        {showAverageRating && (
          <div className="testimonials-average-rating flex justify-start items-center lg:gap-4">
             <div className="rating-text text-center">
              <span className="h4">{averageRating.toFixed(1)}</span>
            </div>
            <div className="stars-container flex justify-center">
              {[...Array(5)].map((_, i) => (
                <div key={`avg-star-${i}`} className="star-wrapper mx-1">
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 24 24" 
                    fill={i < Math.round(averageRating) ? starColor : "none"}
                    stroke={starColor}
                    strokeWidth="1.5"
                    className="w-6 h-6"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                  </svg>
                </div>
              ))}
            </div>
           
          </div>
        )}
        
        {section.title && (
          <Headings 
            text={section.title} 
            type={isMobile ? section.header_style_mobile : section.header_style} 
            classes={`text-left lg:pr-[30px]`}
          />
        )}
        
        {section.subtext && contentBeneathTitle && section.enable_read_more && (
           
           <ReadMoreParagraph 
              text={section.subtext} 
              maxChars={section.read_more_max_chars}
              type={isMobile ? section.subtext_style_mobile : section.subtext_style}

            />
        )}

      {section.subtext && contentBeneathTitle && !section.enable_read_more && (
           
           <Paragraph 
              text={section.subtext} 
              type={isMobile ? section.subtext_style_mobile : section.subtext_style}
              classes={`text-left`}
            />
        )}
      </div>
     
      <div className={clsx("testimonials-content", {
        "flex flex-col lg:flex-row gap-x-[20px] gap-y-[20px]": sideLayout,
        "grid grid-cols-1 lg:grid-cols-2 gap-x-[20px] gap-y-[20px]": mosaicGrid && !sideLayout
      })}>
        {sideLayout ? (
          <>
            <div 
              className="testimonials-left-column" 
              style={{ width: `${leftColumnWidth}%` }}
            >              
              {/* Side by side layout - Left column scrolling (featured testimonial + duplicates) */}
              <div className="scroll-column scroll-down" style={{animationDuration: `${scrollSpeed}s`,  '--scroll-distance': `${scrollDistancePx}px`  }}>
                {blocks.map((blockItem, index) => (
                  <TestimonialCard 
                    key={`featured-dup-${index}`}
                    blockItem={blockItem} 
                    isMobile={isMobile} 
                    starColor={starColor}
                    textType={textStyle || "b1"}
                    authorType={authorStyle || "bfb"}
                    subtextType={subTextType || "b3"}
                    truncateText={truncateText}               
                    contentGap={blockSpacing}
                    featured={index === 0}
                  />
                ))}
              </div>
            </div>

            <div 
              className="testimonials-right-column" 
              style={{ width: `${rightColumnWidth}%` }}
            >
              {/* Side by side layout - Right column scrolling in reverse */}
              <div className="scroll-column scroll-up" style={{ animationDuration: `${scrollSpeedReverse}s`,  '--scroll-distance': `${scrollDistancePx}px` }}>
                
                {blocks.reverse().map((blockItem, index) => (
                  <TestimonialCard 
                    key={`review-rev-dup-${index}`}
                    blockItem={blockItem} 
                    isMobile={isMobile} 
                    starColor={starColor}
                    textType={textStyle || "b1"}
                    authorType={authorStyle || "bfb"}
                    subtextType={subTextType || "b3"}
                    truncateText={truncateText}
                    contentGap={blockSpacing}
                  />
                ))}
              </div>
            </div>
          </>
        ) : (
          mosaicGrid ? (
            blocks.map((blockItem, index) => (
              <TestimonialCard 
                key={`review-${index}`}
                blockItem={blockItem} 
                isMobile={isMobile} 
                starColor={starColor}
                textType={textStyle || "b1"}
                authorType={authorStyle || "bfb"}
                subtextType={subTextType || "b3"}
                truncateText={truncateText}
                contentGap={blockSpacing}
                featured={index === 0}
              />
            ))
          ) : (
            <div className="testimonials-infinite-scroll">
              <div className="scroll-column scroll-down" style={{ animationDuration: `${scrollSpeed}s` }}>
                {/* Original testimonials */}
                {blocks.map((blockItem, index) => (
                  <TestimonialCard 
                    key={`review-${index}`}
                    blockItem={blockItem} 
                    isMobile={isMobile} 
                    starColor={starColor}
                    textType={textStyle || "b1"}
                    authorType={authorStyle || "bfb"}
                    subtextType={subTextType || "b3"}
                    truncateText={truncateText}
                    contentGap={blockSpacing}
                    featured={index === 0}
                  />
                ))}
              </div>
              
              <div className="scroll-column scroll-up" style={{ animationDuration: `${scrollSpeedReverse}s` }}>
                {/* Original testimonials */}
                {blocks.reverse().map((blockItem, index) => (
                  <TestimonialCard 
                    key={`review-rev-${index}`}
                    blockItem={blockItem} 
                    isMobile={isMobile} 
                    starColor={starColor}
                    textType={textStyle || "b1"}
                    authorType={authorStyle || "bfb"}
                    subtextType={subTextType || "b3"}
                    truncateText={truncateText}
                    maxChars={section.read_more_max_chars}
                    contentGap={blockSpacing}
                  />
                ))}
       
              </div>
            </div>
          )
        )}
      </div>
      
    </div>
  );
};

function ReadMoreParagraph({ text, maxChars = 150, type }) {
  const [expanded, setExpanded] = useState(false);
  const isTruncated = text.length > maxChars;
  const displayText = expanded || !isTruncated ? text : text.slice(0, maxChars) + '...';

  return (
    <div className="w-full flex flex-col items-start gap-2">
      <Paragraph text={displayText} classes="text-left"  type={type} />
      {isTruncated && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="underline"
        >
          {expanded ? 'Read Less' : 'Read More'}
        </button>
      )}
    </div>
  );
}

// Testimonial Card Component for reusability
const TestimonialCard = ({ 
  blockItem, 
  isMobile, 
  starColor, 
  textType,
  authorType,
  subtextType,
  truncateText,
  maxChars,
  contentGap,
  featured = false
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const hasImage = blockItem.settings.image;
  const hasCompanyLogo = blockItem.settings.company_logo;

  return (
    <div className={clsx(
      "testimonial-card p-8", 
      featured ? "featured-testimonial" : "",
      hasImage ? "has-image" : "",
      hasCompanyLogo ? "has-company-logo" : ""
    )}>
      {hasCompanyLogo && (
        <div className="testimonial__company-logo mb-4">
          <Image
            imageUrl={blockItem.settings.company_logo}
            alt={"Company Logo"}
            className="max-h-12"
          />
        </div>
      )}
      
      {(blockItem.settings.star_rating > 0) && (
        <div className="testimonial__ratings flex"   style={{ marginBottom: `${contentGap}px` }}>
          {[...Array(5)].map((_, i) => (
            <React.Fragment key={`star-${i}`}>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill={i < blockItem.settings.star_rating ? starColor : "none"}
                stroke={starColor}
                strokeWidth="1.5"
                className="w-5 h-5 mr-1"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
              </svg>
            </React.Fragment>
          ))}
        </div>
      )}
      
      <div 
        className={clsx("testimonial__quote text-black", textType)}
        style={{ marginBottom: `${contentGap}px` }}
      >
        {blockItem.settings.title && (
          <div className="testimonial-title mb-2 font-medium">{blockItem.settings.title}</div>
        )}
        
        <Paragraph
          text={expanded ? blockItem.settings.blurb : truncateText(blockItem.settings.blurb, maxChars)}
          type={textType}
        />
        
        {!expanded && blockItem.settings.blurb && blockItem.settings.blurb.length > maxChars && (
          <button 
            onClick={() => setExpanded(true)}
            className="read-more-button text-blue-600 hover:underline mt-2"
          >
            Read more
          </button>
        )}
      </div>
      
      <div className="testimonial-data flex items-center">
        {hasImage && (
          <div className="testimonial__image mr-4">
            <Image
              imageUrl={blockItem.settings.image}
              alt={"Testimonial author"}
              classes="w-12 h-12 rounded-full object-cover"
            />
          </div>
        )}
        
        <div className="testimonial__details text-black">
          {blockItem.settings.author && (
        
             <Paragraph
             text={blockItem.settings.author}
               type={authorType}
               classes="testimonial__author"
           />
          )}
          {blockItem.settings.subtext && (
        
                <Paragraph
                  text={`"${blockItem.settings.subtext}"`}
                  type={subtextType}
                  classes="testimonial__subtext"
                
              />
          )}
        </div>
      </div>
    </div>
  );
};

export default ScrollingLayout;